import {LiveAnnouncer} from '@angular/cdk/a11y';
import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild,} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSort, Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {ActivatedRoute, NavigationStart, ParamMap, Router,} from '@angular/router';
import {ShowDialogsComponent} from 'src/app/components/show-dialogs/show-dialogs.component';
import {DataManagementAppService} from 'src/app/services/data-management-app.service';
import {SharedFeaturesService} from 'src/app/services/shared-features.service';
import {WebSocketService} from 'src/app/services/web-socket.service';
import {ShowWebSocketResponseComponent} from '../../show-web-socket-response/show-web-socket-response.component';
import {UploadDataGuard} from '../../../../guards/upload-data.guard';
import {Observable, Subscription} from 'rxjs';

interface FileElement {
  name: string;
  size: number;
  type: string;
}

@Component({
  selector: 'app-upload-files',
  template: `
    <div class="content-page">
      <!-- PAGE HEADER -->
      <div class="pagehead" *ngIf="dataSourceDetails">
        <h1>{{ dataSourceName | textpretty }}</h1>
        <div class="meta">
          <span>Created
            <b>{{ dataSourceDetails?.created_dtm | utcToLocal | date : 'mediumDate' }}</b></span>
          <span>Last updated
            <b>{{ dataSourceDetails?.updated_dtm | utcToLocal | date : 'mediumDate' }}</b></span>
        </div>
      </div>

      <div class="pagehead" *ngIf="!dataSourceDetails">
        <h1>New Dataset</h1>
        <mat-form-field class="example-full-width name-field">
          <mat-label>Enter DataSource Name</mat-label>
          <input matInput [(ngModel)]="dataSourceName"/>
        </mat-form-field>
      </div>

      <!-- SPINNER (logic preserved: shown when !waiting) -->
      <div
        *ngIf="!waiting"
        class="fixed inset-0 flex justify-center items-center z-50 mt-10"
      >
        <mat-spinner></mat-spinner>
      </div>

      <div *ngIf="waiting" class="stack">
        <!-- UPLOAD CARD -->
        <div class="panel">
          <div class="panel-h">Upload files</div>
          <div class="panel-b">
            <div
              class="drop"
              [class.drag-active]="dragActive"
              (dragover)="onDragOver($event)"
              (dragleave)="onDragLeave($event)"
              (drop)="onDrop($event)"
            >
              <span class="cloud">
                <mat-icon>cloud_upload</mat-icon>
              </span>
              <div class="txtwrap">
                <div class="big">
                  Drag &amp; drop .xlsx files here, or
                  <span class="browse" (click)="onClick($event)">browse</span>
                </div>
                <div class="hint">
                  Excel (.xlsx) only · up to {{ numberOfFliesAllowed }} files ·
                  {{ fileUploadSize }} MB each
                </div>
              </div>
              <button
                type="button"
                class="btn-ghost browse-btn"
                (click)="onClick($event)"
              >
                Browse files
              </button>
            </div>

            <input
              #fileUpload
              type="file"
              (change)="onFileChange($event)"
              multiple
              accept=".xlsx"
              class="hidden-display"
            />

            <p *ngIf="limitExceedFiles" class="helper-error">
              Maximum upload limit reached.
            </p>
            <p *ngIf="errorMessage" class="helper-error">
              {{ errorMessage }}
            </p>

            <!-- STAGED FILES -->
            <div *ngIf="files.length > 0" class="staged">
              <div
                class="staged-row"
                *ngFor="let f of files; let i = index"
              >
                <span class="xls"><mat-icon>description</mat-icon></span>
                <span class="nm" [matTooltip]="f.name">{{ f.name }}</span>
                <span class="sz">{{ (f.size / (1024 * 1024)) | number : '1.0-2' }} MB</span>
                <button
                  class="rm"
                  matTooltip="Remove"
                  (click)="onRemoveFile(i)"
                >
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
            <div *ngIf="files.length === 0" class="empty">
              No files staged yet — drop files above to get started.
            </div>
          </div>
        </div>

        <!-- UPLOADED FILES CARD -->
        <div class="panel">
          <div class="toolbar">
            <div class="panel-title">Uploaded files</div>
            <div class="searchbox">
              <mat-icon class="search-ico">search</mat-icon>
              <input
                (keyup)="applyFilter($event)"
                #input
                placeholder="Search files"
              />
            </div>
          </div>

          <div class="table-wrap">
            <table
              mat-table
              [dataSource]="dataSource"
              matSort
              (matSortChange)="announceSortChange($event)"
              class="upload-table"
            >
              <ng-container matColumnDef="index">
                <th mat-header-cell *matHeaderCellDef>Index</th>
                <td mat-cell *matCellDef="let element; let i = index">
                  {{ getDisplayIndex(i) }}
                </td>
              </ng-container>

              <ng-container matColumnDef="file_name">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  mat-sort-header
                  sortActionDescription="Sort by file_name"
                >
                  Name
                </th>
                <td mat-cell *matCellDef="let element" class="name-col">
                  <span class="fname">
                    <span class="xls"><mat-icon>description</mat-icon></span>
                    <span class="fname-text" [matTooltip]="element.file_name">
                      {{ element.file_name }}
                    </span>
                  </span>
                </td>
              </ng-container>

              <ng-container matColumnDef="update_dtm">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  mat-sort-header
                  sortActionDescription="Sort by Uploaded Date"
                >
                  Uploaded
                </th>
                <td mat-cell *matCellDef="let element">
                  {{ element.update_dtm | utcToLocal | date : 'mediumDate' }}
                </td>
              </ng-container>

              <ng-container matColumnDef="status">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  mat-sort-header
                  sortActionDescription="Sort by Status"
                >
                  Status
                </th>
                <td mat-cell *matCellDef="let element; let i = index">
                  <ng-container *ngIf="!element?.status; else success">
                    <span
                      class="badge bad"
                      (click)="showError(element)"
                    >Failed</span>
                  </ng-container>

                  <ng-template #success>
                    <ng-container *ngIf="isObjectEmpty(element?.failure?.info); else warning">
                      <span
                        class="badge"
                        [class.pend]="element.status === 'Pending'"
                        [class.ok]="element.status !== 'Pending'"
                      >{{ element.status === 'Pending' ? 'Pending' : 'Success' }}</span>
                    </ng-container>
                    <ng-template #warning>
                      <span
                        class="badge warn"
                        matTooltip="Click to review suggestions"
                        (click)="showError(element)"
                      >Success · {{ infoCount(element) }} suggestion{{ infoCount(element) > 1 ? 's' : '' }}</span>
                    </ng-template>
                  </ng-template>
                </td>
              </ng-container>

              <ng-container matColumnDef="action">
                <th mat-header-cell *matHeaderCellDef class="action-col">Action</th>
                <td mat-cell *matCellDef="let element; let i = index" class="action-col">
                  <button
                    *ngIf="element.status !== 'Pending'"
                    class="iconbtn dl"
                    matTooltip="Download"
                    (click)="downloadFile(element.file_name)"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12M7 10l5 5 5-5"/><path d="M5 21h14"/></svg>
                  </button>
                  <button
                    class="iconbtn"
                    matTooltip="Delete"
                    (click)="deleteFile(element.file_name, i)"
                  >
                    <mat-icon>delete</mat-icon>
                  </button>
                </td>
              </ng-container>

              <tr
                mat-header-row
                *matHeaderRowDef="displayedColumns; sticky: true"
              ></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: displayedColumns"
              ></tr>
            </table>
          </div>

          <mat-paginator
            [pageSizeOptions]="[5, 10, 15, 20]"
            aria-label="Select page of periodic elements"
            (page)="pageEvent = $event"
          >
          </mat-paginator>
        </div>

        <!-- SAVE BAR -->
        <div class="savebar">
          <span
            class="why"
            *ngIf="fileUploadProcessingFlag || files.length == 0 || datasetNameValidation()"
          >Add at least one file and a dataset name to save</span>
          <button
            (click)="uploadFiles()"
            class="btn-primary"
            matTooltip="Add at least one file and a dataset name to save"
            [disabled]="fileUploadProcessingFlag || (files.length == 0 || datasetNameValidation())"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./upload-files.component.scss'],
})
export class UploadFilesComponent
  implements OnInit, OnDestroy, UploadDataGuard {
  waiting: boolean = true;
  dataSourceName: string | any = '';
  availableDataSources = [''];
  files: File[] = [];
  uploadFileDataSource: any;
  statusOfUploadedFiles: any = {};
  updateStatus = false;
  existingFiles: any;
  fileUploadProcessingFlag: boolean = false;
  dataSourceDetails: any;
  @ViewChild('fileUpload')
  fileUpload: ElementRef | any;
  fileUploadSize = 0;
  numberOfFliesAllowed = 0;
  limitExceedFiles: any = false;
  selectedFileSize = 0;
  displayedColumns = ['index', 'file_name', 'update_dtm', 'action', 'status'];
  displayedColumnsToUpload = ['type', 'name', 'size'];
  @ViewChild(MatSort) sort: MatSort | any;
  @ViewChild(MatPaginator) paginator: MatPaginator | any;
  pageEvent: PageEvent | any;
  dataSource: any;
  errorMessage = '';
  nextUrl = '';
  dragActive = false;
  private subscription: Subscription | any;

  constructor(
    private dataManagementServices: DataManagementAppService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: SharedFeaturesService,
    private dialog: MatDialog,
    private _liveAnnouncer: LiveAnnouncer,
    private webSocketServices: WebSocketService
  ) {
  }

  ngOnInit(): void {
    this.subscription = this.webSocketServices
      .getMessages()
      .subscribe((response: any) => {
        if (response?.message) {
          const {type, message} = response.message;
          if (type === 'upload_file_status') {
            this.updateFileStatus(message.file_name, message.success_flag, {
              "failure": message.failure,
              "info": message.info
            })
          }
          // CHANGED: Handle validation errors from backend - show snackbar instead of silent fail
          if (type === 'upload_file_error') {
            this.snackBar.openSnackBar(
              message || 'An error occurred during file validation',
              'Close',
              'error-snackbar'
            );
          }
          if (type === 'upload_file_completion') {
            this.fileUploadProcessingFlag = false
            this.getDataManagementEntry();
          }
        }
      });
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.nextUrl = event.url;
      }
    });
    this.webSocketServices.connect();
    this.dataManagementServices.getUploadFilesConfiguration().subscribe({
      next: (response: any) => {
        this.fileUploadSize = response?.FILE_SIZE_OF_DATA_SOURCE;
        this.numberOfFliesAllowed = response?.DATA_UPLOAD_MAX_NUMBER_FILES;
      },
    });
    this.route.paramMap.subscribe((params: ParamMap) => {
      const dataName = params.get('file_name');
      if (dataName == null || dataName == 'upload-file') {
        this.dataSourceName = '';
      } else {
        this.dataSourceName = dataName;
      }
    });
    if (this.dataSourceName) {
      this.getDataManagementEntry();
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    // Intentionally NOT disconnecting: the socket is app-wide (task status
    // toasts in AppComponent share it); closing it here would kill those.
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.files && this.files.length > 0) {
      if (this.nextUrl == '/account') {
        return true;
      } else {
        return confirm(
          'You have unsaved changes. Do you really want to leave?'
        );
      }
    }
    return true;
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if (this.files && this.files.length > 0) {
      $event.returnValue = true;
    }
  }

  infoCount(element: any): number {
    const info = element?.failure?.info;
    if (!info || typeof info !== 'object') return 0;
    const distinct = new Set<string>();
    let plain = 0;
    for (const messages of Object.values(info)) {
      for (const entry of (Array.isArray(messages) ? messages : [messages])) {
        if (entry && typeof entry === 'object' && (entry as any).type === 'part_number_suggestions') {
          for (const s of (entry as any).suggestions || []) {
            distinct.add(`${s.value} ${s.suggestion ?? ''}`);
          }
        } else {
          plain += 1;
        }
      }
    }
    return distinct.size + plain;
  }

  isObjectEmpty(data: any): boolean {
    return (
      typeof data === 'object' &&
      data !== null &&
      Object.keys(data).length === 0
    );
  }

  getDisplayIndex(index: number): number {
    return this.paginator.pageIndex * this.paginator.pageSize + index + 1;
  }


  updateFileStatus(fileName: string, newStatus: string, failureData: any): void {
    const updatedData = this.dataSource.data.map((item: any) =>
      item.file_name === fileName ? {...item, status: newStatus, failure: failureData} : item
    );

    this.existingFiles = this.existingFiles.map((item: any) =>
      item.file_name === fileName ? {...item, status: newStatus, failure: failureData} : item
    );

    this.dataSource.data = updatedData;
  }


  onClick(event: MouseEvent) {
    if (this.fileUpload) this.fileUpload.nativeElement.click();
  }

  getFileStatusObject(fileName: any): any {
    const fileStatus = this.statusOfUploadedFiles[fileName];
    if (fileStatus) {
      return {
        file_name: fileStatus?.file_name,
        success_flag: fileStatus?.success_flag,
        failure: {
          failure: fileStatus?.failure,
          info: fileStatus?.info,
        },
      };
    } else {
      return false;
    }
  }

  showError(fileObject: any) {
    const dialogRef = this.dialog.open(ShowWebSocketResponseComponent, {
      data: {data: fileObject?.failure, file_name: fileObject?.file_name},
      disableClose: false,
      panelClass: 'd2c-error-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => {
      // "Re-upload" in the error dialog reopens the file picker so the user
      // can select a corrected file for this data source.
      if (result === 'reupload') {
        this.fileUpload?.nativeElement?.click();
      }
    });
  }

  getDataManagementEntry() {
    this.dataManagementServices
      .getDataManagemententry(this.dataSourceName)
      .subscribe({
        next: (response: any) => {
          this.dataSourceDetails = response;
          this.updateStatus = true;
          this.loadExistingFiles();
        },
      });
  }

  setDataSourceName() {
    this.dataSourceName = this.dataSourceName.trim().toLowerCase();
    if (this.availableDataSources.includes(this.dataSourceName)) {
      this.snackBar.openSnackBar(
        'data-source name already exist',
        'Close',
        'error-snackbar'
      );

      return;
    }
    if (this.dataSourceName.trim().toLowerCase() == '') {
      this.snackBar.openSnackBar(
        'invalid datasource name',
        'Close',
        'error-snackbar'
      );
    }

    this.router.navigate(['../', this.dataSourceName], {
      relativeTo: this.route,
    });
  }

  datasetNameValidation() {
    this.dataSourceName = this.dataSourceName.trim().toLowerCase();
    if (this.dataSourceName) return false;
    return true;
  }

  loadExistingFiles() {
    this.dataManagementServices
      .getUploadedFiles(this.dataSourceName)
      .subscribe({
        next: (response: any) => {
          if (response[this.dataSourceName]) {
            this.existingFiles = response[this.dataSourceName];
            this.syncPreviewOfLoadedFiles(this.existingFiles);
          }
        },

        error: (error: any) => {
          this.existingFiles = [];
          this.syncPreviewOfLoadedFiles(this.existingFiles);
          console.error('An error occurred', JSON.stringify(error));
        },
      });
  }

  syncPreviewOfLoadedFiles(files: any) {
    this.dataSource = new MatTableDataSource(files);
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sortingDataAccessor = (data: any, sortHeaderId: any) => {
      const value = data[sortHeaderId];
      return typeof value === 'string' ? value.toLowerCase() : value;
    };
  }

  announceSortChange(sortState: Sort | any) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onFileChange(event: any) {
    this.processSelectedFiles(event.target.files);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragActive = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragActive = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragActive = false;
    if (event.dataTransfer?.files) {
      this.processSelectedFiles(event.dataTransfer.files);
    }
  }

  private processSelectedFiles(fileList: FileList) {
    this.errorMessage = '';
    this.selectedFileSize = this.files.reduce(
      (totalSize: number, file: any) => totalSize + file.size / (1024 * 1024),
      0
    );

    const newFiles = Array.from(fileList).filter(
      (file: any) =>
        file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
        !this.files.some((existingFile) => existingFile.name === file.name)
    );

    const unsupportedFiles = Array.from(fileList)
      .filter(
        (file: any) =>
          file.type !== 'application/vnd.ms-excel' &&
          file.type !==
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )
      .map((file: any) => file.type);

    const unsupportedFileTypes = unsupportedFiles.join(', ');

    if (unsupportedFileTypes) {
      this.errorMessage = `These files are not supported : ${unsupportedFileTypes}`;
    }

    const newFilesTotalSize = newFiles.reduce(
      (totalSize: number, file: any) => totalSize + file.size / (1024 * 1024),
      0
    );

    if (this.selectedFileSize + newFilesTotalSize <= this.fileUploadSize) {
      if (this.files.length + newFiles.length <= this.numberOfFliesAllowed) {
        newFiles.forEach((file: any) => {
          this.files.push(file);
        });
        this.selectedFileSize += newFilesTotalSize;
        this.limitExceedFiles = false;
      } else {
        this.files = [];
        this.errorMessage = 'Maximum number of files limit reached';
      }
    } else {
      this.limitExceedFiles = true;
    }

    this.syncPreview();
  }

  syncPreview() {
    this.uploadFileDataSource = new MatTableDataSource(this.files);
  }

  onRemoveFile(index: number) {
    this.files.splice(index, 1);
    this.syncPreview();
    this.fileUpload.nativeElement.value = '';
  }

  arrangeFileForPreview(files: any) {
    const currentTimestamp = new Date().toISOString();
    const previewFiles = files.map((value: any) => {
      return {
        "file_name": value.name,
        "file_url": "",
        "update_dtm": currentTimestamp,
        "status": 'Pending',
        "failure": {
          "info": {},
          "failure": {}
        }
      };
    });
    return previewFiles;

  }

  combineAndUpdateFiles(oldArray: any[] = [], newArray: any[] = []): any[] {
    if (!oldArray || oldArray.length === 0) {
      return newArray || [];
    }
    if (!newArray || newArray.length === 0) {
      return oldArray || [];
    }
    const fileMap = new Map<string, any>();
    oldArray.forEach((item) => fileMap.set(item.file_name, item));
    newArray.forEach((item) => fileMap.set(item.file_name, item));
    return Array.from(fileMap.values());
  }


  uploadFiles() {
    if (this.files.length === 0) {
      console.error('No files selected for upload.');
      return;
    }
    this.existingFiles = this.combineAndUpdateFiles(this.existingFiles, this.arrangeFileForPreview(this.files))
    this.syncPreviewOfLoadedFiles(this.existingFiles)
    this.fileUploadProcessingFlag = true;
    this.dataManagementServices
      .uploadFiles(this.files, this.dataSourceName, this.updateStatus)
      .subscribe({
        next: (response: any) => {
          this.snackBar.openSnackBar(
            response?.message,
            'Close',
            'success-snackbar'
          );
          this.files = [];
          this.syncPreview();
          this.fileUpload.nativeElement.value = '';
          if (!this.updateStatus) {
            this.router.navigate(['../', this.dataSourceName], {
              relativeTo: this.route,
            });
          }
        },
        error: (error: any) => {
          this.fileUploadProcessingFlag = false;
          console.error('An error occurred while uploading files:', error);
          this.snackBar.openSnackBar(
            error?.error?.message || error?.msg || error?.error?.msg || 'An error occurred while uploading files',
            'Close',
            'error-snackbar'
          );
        },
      });
  }

  downloadFile(fileName: string) {
    this.dataManagementServices
      .downloadSingleFile(this.dataSourceName, fileName)
      .subscribe({
        next: (blob: Blob) => {
          this.dataManagementServices.saveCsvFile(blob, fileName);
        },
        error: async (error: any) => {
          let message = 'Failed to download file';
          try {
            const text = await error.error.text();
            message = JSON.parse(text).message || message;
          } catch (e) {
          }
          this.snackBar.openSnackBar(message, 'Close', 'error-snackbar');
        },
      });
  }

  deleteFile(fileName: string, index: number) {
    const dialogRef = this.dialog.open(ShowDialogsComponent, {
      data: {
        title: 'Delete file',
        message: `Delete '${fileName}'? This permanently removes the file from this data source.`,
        confirmText: 'Delete',
        danger: true,
      },
      disableClose: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dataManagementServices
          .deleteSingleFile(this.dataSourceName, fileName)
          .subscribe({
            next: (response: any) => {
              this.getDataManagementEntry();
              this.snackBar.openSnackBar(
                response.message,
                'Close',
                'success-snackbar'
              );
            },
            error: (error: any) => {
              this.snackBar.openSnackBar(
                error.error.msg,
                'Close',
                'error-snackbar'
              );
            },
          });
      }
    });
  }
}
